import { gql } from '@apollo/client'

import {
	EMPLOYER_FRAGMENT,
	IMAGE_FILE_FRAGMENT,
	WORKED_TIMESHEET_FRAGMENT,
	WORKER_CERTIFICATION_FRAGMENT,
	WORKER_CREDENTIAL_FRAGMENT,
} from '../apollo.fragment'
import { WORKER_FRAGMENT } from '../fragments'

export const VERIFY_WORKER_QUERY = gql`
	query verifyWorker($token: String!) {
		workerToken(token: $token) {
			token
			notificationChannel
			worker {
				...workerFragment
				recruiter {
					id
					name
					phone
					email
					title
				}
				company {
					address
					id
					country
					phone
				}
			}
		}
	}

	${WORKER_FRAGMENT}
`

export const GET_WORKER_DETAIL_QUERY = gql`
	query getWorkerDetailQuery {
		worker {
			...workerFragment
		}
	}
	${WORKER_FRAGMENT}
`

export const GET_PROFILE_BASIC_INFO_QUERY = gql`
	query getProfileQuery {
		worker {
			id
			firstName
			lastName
			email
			phone
			phoneVerifiedAt
			preferredShifts
			preferredWorkTypes
			workingPreferredLocations {
				id
				city
				state
				isPrimary
			}
			addresses {
				id
				aptNumber
				city
				country
				default
				street
				state
				zipcode
			}
			resumes {
				documentType
				id
				file {
					contentType
					filename
					fileUrl
					blobId
					id
				}
			}
		}
	}
`

export const GET_WORKER_RESUMES_QUERY = gql`
	query getProfileQuery {
		worker {
			id
			resumes {
				documentType
				id
				file {
					contentType
					filename
					fileUrl
					blobId
					id
				}
			}
		}
	}
`

export const GET_PROFILE_PHONE_VERIFIED_QUERY = gql`
	query getProfileQuery {
		worker {
			phoneVerifiedAt
			phone
		}
	}
`

export const GET_EMERGENCY_CONTACTS_QUERY = gql`
	query getProfileQuery {
		worker {
			workerEmergencyContacts {
				id
				firstName
				lastName
				phone
				relationship
			}
		}
	}
`

export const GET_DISCIPLINES_SPECIALTIES_QUERY = gql`
	query getProfileQuery {
		worker {
			workerDisciplineSpecialties {
				id
				discipline {
					disciplineName
					disciplineType
					id
					hasSkillChecklist
				}
				specialty {
					name
					id
					hasSkillChecklist
				}
				isPrimary
			}
		}
	}
`

export const GET_WORKER_BASIC_INFO_QUERY = gql`
	query getWorkerBasicInfoQuery {
		worker {
			verifiedPreferredLocations
			email
			firstName
			id
			lastName
			phone
			isActiveStatus
			phoneVerifiedAt
			status
			workAuthorizedAt
			workerBadges {
				id
				badgeType
			}
			preferredShifts
			preferredWorkTypes
			workingPreferredLocations {
				id
				city
				state
				isPrimary
			}
			sclProvider {
				provider
				providerId
			}
			workerDisciplineSpecialties {
				id
				discipline {
					disciplineName
					disciplineType
					id
				}
				specialty {
					name
					id
				}
				isPrimary
			}
			employer {
				...employerFragment
			}
			secondEmployer {
				...employerFragment
			}
			countryOfBirth
		}
	}
	${EMPLOYER_FRAGMENT}
`

export const GET_WORKER_BEST_MATCHES_JOB_PARAM_QUERY = gql`
	query getWorkerBestMatchesJobParamQuery {
		worker {
			workerDisciplineSpecialties {
				id
				discipline {
					disciplineName
					disciplineType
					id
				}
				specialty {
					name
					id
				}
				isPrimary
			}
			educationLevel
			yearsOfTotalExperience
		}
	}
`

export const GET_WORKER_ONBOARDING_INFO = gql`
	query getWorkerOnboardingInfoQuery {
		workerOnboarding {
			id
			state
			completedPercent
			onboardingSteps {
				id
				state
				stepName
				completedPercent
				stepInfo {
					fieldsDefinition
					id
					description
					stepOrder
					optional
				}
			}
		}
	}
`

export const GET_WORKER_EARNED_BADGES_QUERY = gql`
	query workerBadgesQuery($after: String, $before: String, $first: Int, $last: Int) {
		workerBadges(after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				badge {
					id
					active
					badgeName
					badgeType
					description
					icon {
						id
						file {
							id
							thumbnails
						}
					}
				}
				badgeType
				completedPercent
				status
			}
		}
	}
`

export const GET_WORKER_EARNED_BADGES_ICON_QUERY = gql`
	query workerBadgesQuery($after: String, $before: String, $first: Int, $last: Int) {
		workerBadges(after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				badge {
					id
					badgeName
					icon {
						id
						file {
							id
							thumbnails
						}
					}
				}
			}
		}
	}
`

export const GET_WORKER_ALL_BADGES_QUERY = gql`
	query allBadgesQuery($after: String, $before: String, $first: Int, $last: Int) {
		badges(after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				active
				badgeName
				badgeType
				description
				icon {
					id
					file {
						id
						thumbnails
					}
				}
			}
		}
	}
`

export const GET_WORKER_SKILL_CHECKLISTS_QUERY = gql`
	query workerSkillChecklistsQuery($after: String, $before: String, $first: Int, $last: Int) {
		workerSkillChecklists(after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				assignmentUrl
				completed
				name
				resultFile {
					file {
						...imageFileFragment
					}
					id
					note
				}
				expired
			}
		}
	}
	${IMAGE_FILE_FRAGMENT}
`

export const GET_WORKER_DETAIL_FOR_TIMECARDS_QUERY = gql`
	query getWorkerDetailTimeQuery($id: String!) {
		worker(id: $id) {
			...workerFragment
			workedTimesheets {
				...workedTimesheetFragment
				timesheetImages {
					imageUrls(size: ["100x100", "200x200"])
					...imageFileFragment
				}
			}
		}
	}

	${WORKER_FRAGMENT}
	${WORKED_TIMESHEET_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`

export const GET_CREDENTIALING_URL_QUERY = gql`
	query getWorkerDetailCredentialingQuery($id: String!) {
		worker(id: $id) {
			id
			workAuthorized
			phone
			phoneVerifiedAt
		}
	}
`

export const GET_WORKER_CERTIFICATIONS_QUERY = gql`
	query getWorkerCertificationsQuery($id: String!) {
		workerCertifications(workerId: $id) {
			...workerCertificationFragment
			licenseImages {
				...imageFileFragment
			}
		}
	}

	${WORKER_CERTIFICATION_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`

export const GET_WORKER_CREDENTIALS_QUERY = gql`
	query getWorkerCredentialsQuery($filter: WorkerCredentialStatusEnum, $after: String, $before: String, $first: Int, $last: Int) {
		workerCredentials(filter: $filter, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerCredentialFragment
			}
		}
	}

	${WORKER_CREDENTIAL_FRAGMENT}
`

export const GET_WORKER_SOCIAL_QUERY = gql`
	query getWorkerSocialAuthenticationsQuery($after: String, $before: String, $first: Int, $last: Int) {
		workerSocialAuthentications(after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				email
				id
				providerName
				providerUuid
			}
		}
	}
`

export const GET_WORKER_PARTIAL_SETTINGS_QUERY = gql`
	query getWorkerPartialSettings($workerId: String!) {
		appInit(workerId: $workerId) {
			worker {
				id
				email
			}
		}
	}
`

export const GET_SIGNED_URLS = gql`
	query getSignedUrls($blobIds: [String!]!) {
		signedUrls(blobIds: $blobIds) {
			id
			fileUrl
			blobId
			imageUrls(size: ["100x100", "200x200", "500x500"])
		}
	}
`

export const GET_REMAINING_OTP_REQUESTS_QUERY = gql`
	query checkRemainingOtpRequestsQuery {
		checkRemainingOtpRequests {
			remainingSessionResendCode
			remainingTotalResendCode
		}
	}
`

export const GET_RECRUITER_INFO = gql`
	query getRecruiterInfo {
		worker {
			recruiter {
				companyId
				email
				id
				name
				phone
				title
			}
		}
	}
`

export const GET_MASTER_DATA = gql`
	query getMasterData {
		appInit {
			masterData
		}
	}
`

export const GET_AUTHORIZED_WORKER = gql`
	query getAuthorizedWorker {
		worker {
			workAuthorized
			phone
			lastName
			firstName
			id
			phoneVerifiedAt
			staffingReferralUser {
				applyUrl
				acceptUrl
			}
		}
	}
`

export const GET_WORKER_REFERRAL_LINK = gql`
	query getWorkerReferralLink {
		worker {
			id
			staffingReferralUser {
				applyUrl
				acceptUrl
			}
		}
	}
`

export const GET_WORKER_AVATAR = gql`
	query getProfileAvatarQuery {
		worker {
			avatar {
				file {
					thumbnails
				}
			}
		}
	}
`

export const WORKER_ONBOARDING_QUERY = gql`
	query workerOnboardingQuery {
		workerOnboarding {
			id
			termsOfService {
				id
				content
				description
				acceptTerms
			}
		}
	}
`

export const ACCEPTED_TERMS_OF_SERVICE_QUERY = gql`
	query acceptedOnboardingTermsOfServiceQuery {
		acceptedOnboardingTermsOfService {
			id
			createdAt
			termsOfService {
				id
				description
				content
				acceptTerms
			}
		}
	}
`

export const DOWNLOAD_RESULT_FILE = gql`
	query workerSkillChecklistQuery($id: String!) {
		workerSkillChecklist(id: $id) {
			resultFile {
				id
				file {
					fileUrl
				}
			}
		}
	}
`

/**
 * Specific information for `Profile Completion` feature
 */
export const GET_WORKER_DETAIL_FOR_PROFILE_COMPLETION = gql`
	query getWorkerDetailForProfileCompletion {
		worker {
			fullName
			firstName
			lastName
			initialScreeningResult
			avatar {
				file {
					fileUrl
					thumbnails
				}
			}
		}
	}
`

export const WORKER_RESUME_PARSER = gql`
	query workerResumeParser($workerId: String!) {
		workerResumeParser(workerId: $workerId) {
			firstName
			lastName
			middleName
			phone
			status
			resumeDescription
			workerAddresses {
				street
				aptNumber
				city
				state
				zipcode
				country
			}
			workExperiences {
				facilityName
				title
				startDate
				endDate
				city
				state
				country
				address1
				address2
				zip
			}
			workerEducations {
				schoolName
				city
				state
				startDate
				endDate
				graduationDate
				degreeName
				major
				gpa
			}
		}
	}
`

export const GET_INITIAL_SCREENING_RESUME_PARSER = gql`
	query getInitialScreeningResumeParser($workerId: String!) {
		initialScreeningResumeParser(workerId: $workerId)
	}
`
